import {
  completeUploadResponseSchema,
  configResponseSchema,
  customerJoinResponseSchema,
  customerStartResponseSchema,
  livekitTokenResponseSchema,
  okResponseSchema,
  presignUploadResponseSchema,
  type CompleteUploadRequest,
  type CompleteUploadResponse,
  type ConfigResponse,
  type CustomerJoinRequest,
  type CustomerJoinResponse,
  type CustomerStartRequest,
  type CustomerStartResponse,
  type LivekitTokenResponse,
  type PresignUploadRequest,
  type PresignUploadResponse,
} from '@fastgarage/protocol';

/** Error thrown for failed API calls; `message` is German and user-facing. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface Parser<T> {
  parse(input: unknown): T;
}

const NETWORK_ERROR_MESSAGE =
  'Keine Verbindung zum Server. Bitte prüfen Sie Ihre Internetverbindung.';

async function request<T>(parser: Parser<T>, path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch {
    throw new ApiError(NETWORK_ERROR_MESSAGE, 0);
  }
  if (!res.ok) {
    let message = 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.';
    let code: string | undefined;
    try {
      const body: unknown = await res.json();
      if (body && typeof body === 'object' && 'error' in body) {
        const err = (body as { error?: { code?: unknown; message?: unknown } }).error;
        if (err && typeof err.message === 'string' && err.message.length > 0) {
          message = err.message;
        }
        if (err && typeof err.code === 'string') {
          code = err.code;
        }
      }
    } catch {
      // Non-JSON error body — keep the generic message.
    }
    throw new ApiError(message, res.status, code);
  }
  const json: unknown = await res.json();
  return parser.parse(json);
}

function jsonInit(body: unknown, customerToken?: string): RequestInit {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (customerToken) {
    headers.Authorization = `Bearer ${customerToken}`;
  }
  return { method: 'POST', headers, body: JSON.stringify(body) };
}

/** GET /api/config — LiveKit URL + configurable consent text. */
export function getConfig(): Promise<ConfigResponse> {
  return request(configResponseSchema, '/api/config');
}

/** POST /api/customer-sessions — customer starts a session (code path). */
export function startCustomerSession(body: CustomerStartRequest): Promise<CustomerStartResponse> {
  return request(customerStartResponseSchema, '/api/customer-sessions', jsonInit(body));
}

/** POST /api/join — customer joins a staff-created session via link token or code. */
export function joinSession(body: CustomerJoinRequest): Promise<CustomerJoinResponse> {
  return request(customerJoinResponseSchema, '/api/join', jsonInit(body));
}

/** POST /api/sessions/:id/livekit-token (Bearer customer token). */
export function fetchLivekitToken(
  sessionId: string,
  customerToken: string,
): Promise<LivekitTokenResponse> {
  return request(
    livekitTokenResponseSchema,
    `/api/sessions/${encodeURIComponent(sessionId)}/livekit-token`,
    jsonInit({}, customerToken),
  );
}

/** POST /api/sessions/:id/customer-end (Bearer customer token) — marks the
 * session ended server-side so the dashboard side terminates as well. */
export async function endCustomerSession(
  sessionId: string,
  customerToken: string,
): Promise<void> {
  await request(
    okResponseSchema,
    `/api/sessions/${encodeURIComponent(sessionId)}/customer-end`,
    jsonInit({}, customerToken),
  );
}

/** POST /api/sessions/:id/photos/presign (Bearer customer token). */
export function presignUpload(
  sessionId: string,
  customerToken: string,
  body: PresignUploadRequest,
): Promise<PresignUploadResponse> {
  return request(
    presignUploadResponseSchema,
    `/api/sessions/${encodeURIComponent(sessionId)}/photos/presign`,
    jsonInit(body, customerToken),
  );
}

/** POST /api/sessions/:id/photos/complete (Bearer customer token). */
export function completeUpload(
  sessionId: string,
  customerToken: string,
  body: CompleteUploadRequest,
): Promise<CompleteUploadResponse> {
  return request(
    completeUploadResponseSchema,
    `/api/sessions/${encodeURIComponent(sessionId)}/photos/complete`,
    jsonInit(body, customerToken),
  );
}

/** PUT the photo blob directly to MinIO via the presigned URL. */
export async function putObject(uploadUrl: string, blob: Blob, contentType: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: blob,
    });
  } catch {
    throw new ApiError(NETWORK_ERROR_MESSAGE, 0);
  }
  if (!res.ok) {
    throw new ApiError(`Upload fehlgeschlagen (${res.status})`, res.status);
  }
}
